import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { Select } from "@sylviq/ui-react-web/components/ui/select";
import { type FormEvent, useEffect, useState } from "react";
import {
  type CreatePaymentTermInput,
  createPaymentTerm,
  deactivatePaymentTerm,
  type PaymentTerm,
  type PaymentTermKind,
  listPaymentTerms,
} from "@/lib/api";
import { useOrganization } from "@/lib/organization";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function integer(value: string, field: string, min: number, max: number): number {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < min || parsed > max) {
    throw new Error(`${field} must be an integer from ${min} to ${max}.`);
  }
  return parsed;
}

// The API stores discounts as integer basis points. The form takes a percent
// a person can read, and rejects values finer than a basis point.
function basisPoints(value: string): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) {
    throw new Error("Discount percent must be a number from 0 to 100.");
  }
  const points = Math.round(parsed * 100);
  if (Math.abs(parsed * 100 - points) > 1e-6) {
    throw new Error(
      "Discount percent supports at most two decimals (whole basis points).",
    );
  }
  return points;
}

function formatDiscountPercent(percentBasisPoints: number): string {
  const percent = percentBasisPoints / 100;
  return `${Number.isInteger(percent) ? percent : parseFloat(percent.toFixed(2))}%`;
}

function discountHint(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "Enter a percent — 100 basis points = 1%.";
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return "Enter the discount as a percent, e.g. 2.";
  if (parsed < 0 || parsed > 100) return "Use a percent from 0 to 100.";
  const points = Math.round(parsed * 100);
  if (Math.abs(parsed * 100 - points) > 1e-6) {
    return "Use at most two decimals, e.g. 2 or 2.5.";
  }
  return `${trimmed}% = ${points} basis points.`;
}

function termSchedule(term: PaymentTerm): string {
  if (term.kind === "standard") return `Net ${term.due_days ?? "—"} days`;
  return `Day ${term.day_of_month_due ?? "—"}; next month after ${term.due_next_month_days ?? "—"} days`;
}

function termDiscount(term: PaymentTerm): string {
  if (term.discount_percent_basis_points == null) return "";
  return ` · ${formatDiscountPercent(term.discount_percent_basis_points)} (${term.discount_percent_basis_points} bp) early discount`;
}

export function PaymentTermsTab() {
  const { scope } = useOrganization();
  const organizationId = scope?.organization_id ?? "";
  const legalEntityId = scope?.legal_entity_id ?? "";
  const scopeKey = `${organizationId}:${legalEntityId}`;
  const [terms, setTerms] = useState<PaymentTerm[]>([]);
  const [loadedScopeKey, setLoadedScopeKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [kind, setKind] = useState<PaymentTermKind>("standard");
  const [name, setName] = useState("");
  const [dueDays, setDueDays] = useState("30");
  const [discountDays, setDiscountDays] = useState("");
  const [discountPercent, setDiscountPercent] = useState("");
  const [dayOfMonthDue, setDayOfMonthDue] = useState("15");
  const [dueNextMonthDays, setDueNextMonthDays] = useState("15");
  const [discountDayOfMonth, setDiscountDayOfMonth] = useState("");
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmingDeactivate, setConfirmingDeactivate] =
    useState<PaymentTerm | null>(null);

  useEffect(() => {
    let cancelled = false;
    setTerms([]);
    setLoadedScopeKey("");
    setError(null);
    setLoadError(null);
    if (!organizationId || !legalEntityId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    listPaymentTerms()
      .then((rows) => {
        if (!cancelled) {
          setTerms(rows);
          setLoadedScopeKey(scopeKey);
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) setLoadError(toErrorMessage(cause));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId, organizationId, legalEntityId, scopeKey]);

  const retryLoad = () => {
    if (!organizationId || !legalEntityId) return;
    setLoading(true);
    setLoadError(null);
    listPaymentTerms()
      .then((rows) => {
        setTerms(rows);
        setLoadedScopeKey(scopeKey);
      })
      .catch((cause: unknown) => setLoadError(toErrorMessage(cause)))
      .finally(() => setLoading(false));
  };

  const visibleTerms = scopeKey && loadedScopeKey === scopeKey ? terms : [];

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setFormError(null);
    setNotice(null);
    try {
      const label = name.trim();
      if (!label) throw new Error("Payment term name is required.");
      const percentWasSet = discountPercent.trim() !== "";
      let input: CreatePaymentTermInput;
      if (kind === "standard") {
        const daysWasSet = discountDays.trim() !== "";
        if (daysWasSet !== percentWasSet) {
          throw new Error("Discount days and discount percent must be set together.");
        }
        input = {
          name: label,
          kind,
          due_days: integer(dueDays, "Due days", 0, 999),
          ...(daysWasSet
            ? {
                discount_days: integer(discountDays, "Discount days", 0, 999),
                discount_percent_basis_points: basisPoints(discountPercent),
              }
            : {}),
        };
      } else {
        const discountDayWasSet = discountDayOfMonth.trim() !== "";
        if (discountDayWasSet !== percentWasSet) {
          throw new Error("Discount day and discount percent must be set together.");
        }
        input = {
          name: label,
          kind,
          day_of_month_due: integer(dayOfMonthDue, "Due day of month", 1, 31),
          due_next_month_days: integer(
            dueNextMonthDays,
            "Next-month threshold days",
            1,
            999,
          ),
          ...(discountDayWasSet
            ? {
                discount_day_of_month: integer(
                  discountDayOfMonth,
                  "Discount day of month",
                  1,
                  31,
                ),
                discount_percent_basis_points: basisPoints(discountPercent),
              }
            : {}),
        };
      }
      const created = await createPaymentTerm(input);
      setTerms((previous) => [created, ...previous.filter((term) => term.id !== created.id)]);
      setLoadedScopeKey(scopeKey);
      setCreating(false);
      setName("");
      setNotice(`${created.name} created. Term schedules are immutable; create a new term to change one.`);
    } catch (cause) {
      setFormError(toErrorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async (term: PaymentTerm) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await deactivatePaymentTerm(term.id);
      setTerms((previous) =>
        previous.map((row) => (row.id === term.id ? { ...row, active: false } : row)),
      );
      setNotice(`${term.name} deactivated. Existing documents keep their recorded term.`);
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Payment-term catalog</h2>
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">
            Terms belong to this set of books. Schedule facts cannot be edited or deleted after creation; deactivation only prevents new selections. Discounts are stored as facts and are not automatically applied or posted.
          </p>
        </div>
        <Button type="button" onClick={() => { setFormError(null); setCreating(true); }}>
          New payment term
        </Button>
      </div>
      {error ? <div role="alert" className="text-sm text-[var(--danger)]">{error}</div> : null}
      {notice ? <output className="text-sm text-[var(--success)]">{notice}</output> : null}

      <section aria-label="Payment terms" className="grid gap-2">
        <h3 className="text-sm font-semibold">Existing terms</h3>
        {loadError ? (
          <div role="alert" className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]">
            <p>Couldn&apos;t load payment terms: {loadError}</p>
            <div className="mt-2">
              <Button type="button" variant="secondary" size="sm" onClick={retryLoad} disabled={loading}>
                Retry
              </Button>
            </div>
          </div>
        ) : loading ? (
          <p className="text-sm text-[var(--muted-foreground)]">Loading payment terms…</p>
        ) : visibleTerms.length === 0 ? (
          <p className="text-sm text-[var(--muted-foreground)]">No terms yet — create a standard or date-driven term above.</p>
        ) : (
          <ul className="grid gap-2">
            {visibleTerms.map((term) => (
              <li key={term.id} className="surface flex flex-wrap items-center justify-between gap-3 rounded-xl p-4">
                <div>
                  <p className="font-medium">{term.name} <Badge tone={term.kind === "standard" ? "neutral" : "warning"}>{term.kind === "standard" ? "Standard" : "Date-driven"}</Badge></p>
                  <p className="text-xs text-[var(--muted-foreground)]">{termSchedule(term)}{termDiscount(term)}</p>
                </div>
                {term.active ? (
                  <Button type="button" variant="secondary" size="sm" data-term-id={term.id} disabled={busy} onClick={() => setConfirmingDeactivate(term)}>Deactivate</Button>
                ) : <Badge tone="neutral">Inactive</Badge>}
              </li>
            ))}
          </ul>
        )}
      </section>

      <Dialog
        open={creating}
        onOpenChange={(open) => {
          if (!open && !busy) {
            setCreating(false);
            setFormError(null);
          }
        }}
        title="New payment term"
        description="Create a named schedule once. Existing documents keep the term they recorded."
      >
        <form onSubmit={submit} className="grid gap-4" aria-label="Create payment term">
          <div className="grid gap-3 sm:grid-cols-2">
            <label htmlFor="payment-term-name" className="grid gap-1 text-xs font-medium">
              Term name
              <Input id="payment-term-name" value={name} onChange={(event) => setName(event.target.value)} required maxLength={120} placeholder="Net 30" />
            </label>
            <label htmlFor="payment-term-kind" className="grid gap-1 text-xs font-medium">
              Schedule
              <Select
                id="payment-term-kind"
                value={kind}
                onChange={(value) => setKind(value as PaymentTermKind)}
                options={[
                  { value: "standard", label: "Standard (Net-N)" },
                  { value: "date_driven", label: "Date-driven (day of month)" },
                ]}
              />
            </label>
          </div>

          {kind === "standard" ? (
            <div className="grid gap-3 sm:grid-cols-3">
              <label htmlFor="payment-term-due-days" className="grid gap-1 text-xs font-medium">
                Due days (0–999)
                <Input id="payment-term-due-days" type="number" min={0} max={999} step={1} value={dueDays} onChange={(event) => setDueDays(event.target.value)} required />
              </label>
              <label htmlFor="payment-term-discount-days" className="grid gap-1 text-xs font-medium">
                Discount days (optional)
                <Input id="payment-term-discount-days" type="number" min={0} max={999} step={1} value={discountDays} onChange={(event) => setDiscountDays(event.target.value)} />
              </label>
              <label htmlFor="payment-term-discount-percent" className="grid gap-1 text-xs font-medium">
                Discount percent (optional)
                <Input id="payment-term-discount-percent" type="number" min={0} max={100} step={0.01} value={discountPercent} onChange={(event) => setDiscountPercent(event.target.value)} placeholder="e.g. 2" />
                <span className="text-xs font-normal text-[var(--muted-foreground)]">{discountHint(discountPercent)}</span>
              </label>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <label htmlFor="payment-term-day-of-month" className="grid gap-1 text-xs font-medium">
                Due day of month (1–31)
                <Input id="payment-term-day-of-month" type="number" min={1} max={31} step={1} value={dayOfMonthDue} onChange={(event) => setDayOfMonthDue(event.target.value)} required />
              </label>
              <label htmlFor="payment-term-next-month-days" className="grid gap-1 text-xs font-medium">
                Next-month threshold (1–999 days)
                <Input id="payment-term-next-month-days" type="number" min={1} max={999} step={1} value={dueNextMonthDays} onChange={(event) => setDueNextMonthDays(event.target.value)} required />
              </label>
              <label htmlFor="payment-term-discount-day" className="grid gap-1 text-xs font-medium">
                Discount day of month (optional)
                <Input id="payment-term-discount-day" type="number" min={1} max={31} step={1} value={discountDayOfMonth} onChange={(event) => setDiscountDayOfMonth(event.target.value)} />
              </label>
              <label htmlFor="payment-term-discount-percent" className="grid gap-1 text-xs font-medium">
                Discount percent (optional)
                <Input id="payment-term-discount-percent" type="number" min={0} max={100} step={0.01} value={discountPercent} onChange={(event) => setDiscountPercent(event.target.value)} placeholder="e.g. 2" />
                <span className="text-xs font-normal text-[var(--muted-foreground)]">{discountHint(discountPercent)}</span>
              </label>
            </div>
          )}
          <p className="text-xs text-[var(--muted-foreground)]">
            Date-driven days 29–31 clamp to the target month’s final day. Discounts
            are entered as percent and stored as whole basis points (100 bp = 1%).
          </p>
          {formError ? <div role="alert" className="text-sm text-[var(--danger)]">{formError}</div> : null}
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="secondary" disabled={busy} onClick={() => setCreating(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>{busy ? "Saving…" : "Create term"}</Button>
          </div>
        </form>
      </Dialog>

      <Dialog
        open={confirmingDeactivate !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmingDeactivate(null);
        }}
        title={
          confirmingDeactivate
            ? `Deactivate ${confirmingDeactivate.name}?`
            : "Deactivate this term?"
        }
        description="Deactivation only stops new documents from selecting this term. Documents that already recorded it keep their schedule, and the term stays in this list marked inactive."
      >
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setConfirmingDeactivate(null)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              const term = confirmingDeactivate;
              setConfirmingDeactivate(null);
              if (term) void deactivate(term);
            }}
          >
            Deactivate term
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
